import { stateKeyFor } from '../context/onboardingDraftStorage'

export interface OnboardingBankRepair {
  itemId: string
  institutionName: string
  reconnectMode: 'update' | 'relink'
}

const KEY_PREFIX = 'v1_onboarding_bank_repairs:'
const TTL_MS = 30 * 60 * 1000

const keyFor = (uid: string | undefined) => (uid ? `${KEY_PREFIX}${uid}` : null)

export type PendingBankReconciliation = { itemId: string; disconnected: boolean }
export type BankReconciliationReturn = PendingBankReconciliation & {
  uid: string
  generation: number
}

/** Old Link sessions predate the explicit generation; use their persisted run, never guess. */
export function bankRepairGeneration(uid: string, recorded: number | undefined): number {
  let generation = recorded
  if (generation === undefined) {
    try {
      generation = JSON.parse(
        sessionStorage.getItem(stateKeyFor(uid)) || 'null'
      )?.persistenceGeneration
    } catch {
      // No trustworthy run to attach this repair to.
    }
  }
  if (typeof generation !== 'number' || !Number.isInteger(generation) || generation < 0)
    throw new Error('The bank repair session could not be restored. Return to setup and try again.')
  return generation
}

/** Router state also survives reload when writing the session-storage handoff fails. */
export function bankReconciliationFromReturn(
  state: unknown,
  uid: string | undefined,
  generation: number
): PendingBankReconciliation | null {
  if (!uid || !state || typeof state !== 'object' || !('plaidBankReconciliation' in state))
    return null
  const pending = state.plaidBankReconciliation
  if (!pending || typeof pending !== 'object') return null
  if (
    !('uid' in pending) ||
    pending.uid !== uid ||
    !('generation' in pending) ||
    pending.generation !== generation ||
    !('itemId' in pending) ||
    typeof pending.itemId !== 'string' ||
    !pending.itemId ||
    !('disconnected' in pending) ||
    typeof pending.disconnected !== 'boolean'
  )
    return null
  return { itemId: pending.itemId, disconnected: pending.disconnected }
}
const reconciliationKey = (uid: string, generation: number) =>
  `v1_onboarding_bank_reconciliation:${uid}:${generation}`

// Unlike reconnect hints, unfinished account cleanup must not expire while the
// draft still references removed accounts. A restart uses a different generation.
export function readBankReconciliation(
  uid: string | undefined,
  generation: number
): PendingBankReconciliation | null {
  if (!uid) return null
  try {
    const value = JSON.parse(sessionStorage.getItem(reconciliationKey(uid, generation)) || 'null')
    return value &&
      typeof value.itemId === 'string' &&
      value.itemId &&
      typeof value.disconnected === 'boolean'
      ? { itemId: value.itemId, disconnected: value.disconnected }
      : null
  } catch {
    return null
  }
}

export function writeBankReconciliation(
  uid: string | undefined,
  generation: number,
  pending: PendingBankReconciliation | null
): void {
  if (!uid) return
  try {
    const key = reconciliationKey(uid, generation)
    if (pending) sessionStorage.setItem(key, JSON.stringify(pending))
    else sessionStorage.removeItem(key)
  } catch {
    // The in-memory phase still allows retries when browser storage is blocked.
  }
}

function normalizeRepairs(value: unknown): OnboardingBankRepair[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (repair): repair is OnboardingBankRepair =>
      repair !== null &&
      typeof repair === 'object' &&
      typeof repair.itemId === 'string' &&
      repair.itemId.length > 0 &&
      typeof repair.institutionName === 'string' &&
      (repair.reconnectMode === 'update' || repair.reconnectMode === 'relink')
  )
}

export function readOnboardingBankRepairs(uid: string | undefined): OnboardingBankRepair[] {
  const key = keyFor(uid)
  if (!key) return []
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null')
    if (!value || typeof value.expiresAt !== 'number' || value.expiresAt <= Date.now()) {
      sessionStorage.removeItem(key)
      return []
    }
    return normalizeRepairs(value.repairs)
  } catch {
    return []
  }
}

export function writeOnboardingBankRepairs(
  uid: string | undefined,
  repairs: OnboardingBankRepair[]
): void {
  const key = keyFor(uid)
  if (!key) return
  try {
    if (repairs.length === 0) {
      sessionStorage.removeItem(key)
      return
    }
    sessionStorage.setItem(key, JSON.stringify({ repairs, expiresAt: Date.now() + TTL_MS }))
  } catch {
    // Recovery remains available in-page when session storage is unavailable.
  }
}

export function removeOnboardingBankRepair(
  uid: string | undefined,
  itemId: string | undefined
): void {
  if (!itemId) return
  const remaining = readOnboardingBankRepairs(uid).filter((repair) => repair.itemId !== itemId)
  writeOnboardingBankRepairs(uid, remaining)
}
